import fulfill from '../../fulfill';
import { SideEffectFunction } from '../../sideEffects';
import { CHECKOUT_API_HOSTNAME, CORE_API_HOSTNAME } from '../../env';
import {
    ActionTypes,
    FetchTerminalData,
    FetchTerminalDataResponse,
    CreateCheckoutSession,
    CreateCheckoutSessionResponse
} from './actions';
import { CheckoutConfig, Session } from '../../types/checkout';
import { SessionResponse } from './types';
import { SalesLocation } from '../../types/customers';
import { recursivelyFetchLocations } from '../salesLocationsFetcher';
import { Account } from '../../types/management-auth';
import { SessionMerchant } from './components/VirtualTerminal';
import { formatMoney } from '../../helpers/formatters';

const fetchTerminalData: SideEffectFunction<FetchTerminalData, FetchTerminalDataResponse> = async (action) => {
    const { accountId, fetchLocations } = action.payload;
    const configPromise: Promise<CheckoutConfig> = fulfill.get({
        accountId,
        url: `${CHECKOUT_API_HOSTNAME}/v1/admin/checkout`,
        handlers: {
            200: (checkoutConfig: CheckoutConfig) => checkoutConfig,
            404: () => {},
        },
    });
    const accountPromise: Promise<Account> = fulfill.get({
        accountId,
        url: `${CORE_API_HOSTNAME}/v1/accounts/${accountId}/management/settings`,
        handlers: {
            200: (account: Account) => account,
            404: () => {},
            403: () => {},
        },
    });
    const [config, account] = await Promise.all([configPromise, accountPromise]);

    let locations: SalesLocation[] = [];
    if (fetchLocations) {
        locations = await recursivelyFetchLocations(
            accountId,
            new URLSearchParams([['limit', '100']]),
            []
        );
    }

    return {
        type: ActionTypes.FetchTerminalDataResponse,
        payload: { config, locations, account },
        meta: {
            cause: action,
        },
    };
};

const toSessionMailtoUrl = (session: Session & SessionMerchant, url: string) => {
    const email = session.customer?.email;
    const amount = session.order.amount;
    const currency = session.order.currency;
    const safeAmount = encodeURIComponent(formatMoney(undefined, amount, currency));
    const safeMerchantName = encodeURIComponent(session.merchant?.name || '');
    const safeSessionUrl = encodeURIComponent(url);
    return `mailto:${email}?subject=Your%20payment%20to%20${safeMerchantName}&body=Here%20is%20your%20link%20to%20pay%20${safeAmount}%20to%20${safeMerchantName}%20${safeSessionUrl}`;
};

const createCheckoutSession: SideEffectFunction<CreateCheckoutSession, CreateCheckoutSessionResponse> = async (
    action
) => {
    const { accountId, session, email } = action.payload;
    const result: CreateCheckoutSessionResponse = await fulfill.post({
        accountId,
        headers: {
            'Dintero-System-Plugin-Name': 'Backoffice-digital-terminal-v2',
            'Dintero-System-Plugin-Version': '2',
        },
        url: `${CHECKOUT_API_HOSTNAME}/v1/sessions`,
        json: session,
        handlers: {
            200: (sessionResponse: SessionResponse) => {
                if (email) {
                    window.open(
                        toSessionMailtoUrl(session, sessionResponse.url),
                        '_blank',
                        'noopener'
                    );
                }
                return {
                    type: ActionTypes.CreateCheckoutSessionResponse,
                    payload: {
                        sessionResponse,
                    },
                    meta: {
                        cause: action,
                    },
                };
            },
        },
    });
    return result;
};
const effects = {
    [ActionTypes.FetchTerminalData]: fetchTerminalData,
    [ActionTypes.CreateCheckoutSession]: createCheckoutSession,
};
export default effects;
